// src/components/features/ParkingSessions/StartParkingDrawer.tsx

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Stack,
  Group,
  Text,
  NumberInput,
  Textarea,
  Box,
  Divider,
  SegmentedControl,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useTranslation } from "react-i18next";

import {
  ParkingSessionInput,
  PARKING_SESSION_STATUS,
  UploadedFile,
} from "@/utils/api";
import { localStringToUtc, formatAddressShort, getCurrentLocalDateTimeString } from "@/utils";
import { useUserProfileContext, useActiveParkingContext, useLayoutUI, useDashboardContext, useAppEnvironment } from "@/contexts";
import { useAutoLocation } from "@/hooks/useAutoLocation";
import { GeoCoords } from "@/hooks/useGeolocation";
import { AddressData } from "@/hooks/useGoogleMapsAutocomplete";

import {
  AppDrawer,
  AppIcon,
  AddressAutocompleteInput,
  CurrencySelector,
  LocationPreview,
  ImageCaptureUpload,
  ImageViewer,
} from "@/components/generic";
import { CarSelector } from "@/components/features";
import { TravelSelector } from "@/components/features/Travels";
import {
  ParkingPhotoSection,
  PhotoDisplayData,
  EMPTY_PHOTO_DISPLAY,
} from "./ParkingPhotoSection";

// ============================================
// Types
// ============================================

export interface StartParkingDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Callback when drawer should close */
  onClose: () => void;

  /**
   * Optional override for the start operation.
   * If not provided, uses ActiveParkingContext.startParking and
   * closes the drawer via LayoutUI.setParkingSession(null) on success.
   */
  onStart?: (params: ParkingSessionInput) => Promise<void>;

  /**
   * Optional override for the loading state.
   * If not provided, uses ActiveParkingContext.isStarting.
   */
  loading?: boolean;

  /** Default car ID to pre-select */
  defaultCarId?: string | null;
}

// ============================================
// Constants
// ============================================

/** Duration preset value for "No limit" / free parking */
const DURATION_NONE = "none";

/** Duration preset value for custom input */
const DURATION_CUSTOM = "custom";

/** Quick-select duration presets in minutes */
const DURATION_PRESETS = [
  { value: DURATION_NONE, labelKey: "parking:drawers.start.duration.none" },
  { value: "30", labelKey: "parking:drawers.start.duration.30min" },
  { value: "60", labelKey: "parking:drawers.start.duration.1h" },
  { value: "120", labelKey: "parking:drawers.start.duration.2h" },
  { value: "180", labelKey: "parking:drawers.start.duration.3h" },
  { value: DURATION_CUSTOM, labelKey: "parking:drawers.start.duration.custom" },
];

// ============================================
// Form State
// ============================================

interface FormState {
  carId: string | null;
  travelId: string | null;
  startTime: string;
  durationPreset: string;
  durationMinutes: number | null;
  initialPrice: number | null;
  currency: string | null;
  formattedAddress: string;
  latitude: number | null;
  longitude: number | null;
  notes: string;
  // Photo
  uploadedFileId: string | null;
}

const getInitialFormState = (
  defaultCarId?: string | null,
  defaultCurrency?: string | null
): FormState => ({
  carId: defaultCarId || null,
  travelId: null,
  startTime: getCurrentLocalDateTimeString(),
  durationPreset: DURATION_NONE,
  durationMinutes: null,
  initialPrice: null,
  currency: defaultCurrency || null,
  formattedAddress: "",
  latitude: null,
  longitude: null,
  notes: "",
  uploadedFileId: null,
});

// ============================================
// Dirty-state serializer
// ============================================

const serializeFormSnapshot = (data: FormState): string => {
  return JSON.stringify(data);
};

// ============================================
// Component
// ============================================

const StartParkingDrawerComponent: React.FC<StartParkingDrawerProps> = ({
  opened,
  onClose,
  onStart: onStartProp,
  loading: loadingProp,
  defaultCarId,
}) => {
  const { t } = useTranslation(["parking", "parkingSessions", "common"]);
  const { currency: homeCurrency } = useUserProfileContext();
  const { totalCarsCount } = useDashboardContext();
  const { isMobile } = useAppEnvironment();

  // ── Context resolution ──
  const { startParking, isStarting } = useActiveParkingContext();
  const { setParkingSession } = useLayoutUI();

  const isLoading = loadingProp ?? isStarting;

  // Auto-location: only enabled when drawer is open
  const {
    coords: autoCoords,
    address: autoAddress,
    isReady: isLocationReady,
    isLoading: isLocationLoading,
  } = useAutoLocation({
    enabled: opened,
    reverseGeocode: true,
    maxAgeMs: 5 * 60 * 1000,
    maxDistanceM: 100,
  });

  // Location bias for autocomplete
  const [locationBias, setLocationBias] = useState<GeoCoords | null>(null);

  // Track if we've auto-filled to prevent overwriting user edits
  const [hasAutoFilled, setHasAutoFilled] = useState(false);

  // Form state
  const [formState, setFormState] = useState<FormState>(() =>
    getInitialFormState(defaultCarId, homeCurrency)
  );

  // ── Photo state (display-only, not in dirty tracking) ──
  const [photoDisplay, setPhotoDisplay] = useState<PhotoDisplayData>(EMPTY_PHOTO_DISPLAY);
  const [isCaptureOpen, setIsCaptureOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // ============================================
  // Dirty State Tracking
  // ============================================

  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened) return;
    const current = serializeFormSnapshot(formState);
    setIsDirty(current !== initialSnapshotRef.current);
  }, [formState, opened]);

  // Reset form when drawer opens
  useEffect(() => {
    if (opened) {
      const newFormState = getInitialFormState(defaultCarId, homeCurrency);
      setFormState(newFormState);
      initialSnapshotRef.current = serializeFormSnapshot(newFormState);
      setIsDirty(false);
      setHasAutoFilled(false);
      setLocationBias(null);
      // Reset photo state
      setPhotoDisplay(EMPTY_PHOTO_DISPLAY);
      setIsCaptureOpen(false);
      setIsPreviewOpen(false);
    }
  }, [opened, defaultCarId, homeCurrency]);

  // Update location bias from auto-location
  useEffect(() => {
    if (autoCoords && opened) {
      setLocationBias(autoCoords);
    }
  }, [autoCoords, opened]);

  // Auto-fill location when ready (once per drawer open)
  useEffect(() => {
    if (isLocationReady && autoAddress && opened && !hasAutoFilled) {
      setFormState((prev) => {
        const addressString =
          formatAddressShort(autoAddress) || autoAddress.displayName || "";
        return {
          ...prev,
          formattedAddress: addressString,
          latitude: autoCoords?.latitude ?? null,
          longitude: autoCoords?.longitude ?? null,
        };
      });
      setHasAutoFilled(true);
    }
  }, [isLocationReady, autoAddress, autoCoords, opened, hasAutoFilled]);

  // ============================================
  // Derived
  // ============================================

  const isCustomDuration = formState.durationPreset === DURATION_CUSTOM;
  const showDurationInput = isCustomDuration;
  const showPriceSection = formState.durationPreset !== DURATION_NONE;
  const hasCoordinates = formState.latitude != null && formState.longitude != null;
  const hasPhoto = Boolean(formState.uploadedFileId);

  // ============================================
  // Handlers
  // ============================================

  const handleCarChange = useCallback(
    (_name: string, value: string | null) => {
      setFormState((prev) => ({
        ...prev,
        carId: value,
        travelId: prev.carId !== value ? null : prev.travelId,
      }));
    },
    []
  );

  const handleTravelChange = useCallback(
    (_name: string, value: string | null) => {
      setFormState((prev) => ({ ...prev, travelId: value }));
    },
    []
  );

  const handleStartTimeChange = useCallback((value: string | null) => {
    setFormState((prev) => ({
      ...prev,
      startTime: value || getCurrentLocalDateTimeString(),
    }));
  }, []);

  const handleDurationPresetChange = useCallback((value: string) => {
    setFormState((prev) => {
      const numericValue = parseInt(value, 10);
      const isPresetNumeric = !isNaN(numericValue);

      return {
        ...prev,
        durationPreset: value,
        durationMinutes:
          value === DURATION_NONE
            ? null
            : value === DURATION_CUSTOM
              ? prev.durationMinutes
              : isPresetNumeric
                ? numericValue
                : null,
      };
    });
  }, []);

  const handleDurationMinutesChange = useCallback(
    (value: string | number) => {
      setFormState((prev) => ({
        ...prev,
        durationMinutes: typeof value === "number" ? value : null,
      }));
    },
    []
  );

  const handleInitialPriceChange = useCallback(
    (value: string | number) => {
      setFormState((prev) => ({
        ...prev,
        initialPrice: typeof value === "number" ? value : null,
      }));
    },
    []
  );

  const handleCurrencyChange = useCallback(
    (_name: string | undefined, value: string | null) => {
      setFormState((prev) => ({ ...prev, currency: value }));
    },
    []
  );

  const handleLocationChange = useCallback(
    (address1: string, fullAddress: AddressData | null) => {
      setFormState((prev) => {
        const newState: FormState = {
          ...prev,
          formattedAddress: address1,
        };

        if (fullAddress) {
          if (fullAddress.latitude != null) {
            newState.latitude = fullAddress.latitude;
          }
          if (fullAddress.longitude != null) {
            newState.longitude = fullAddress.longitude;
          }
        }

        return newState;
      });

      if (fullAddress?.latitude && fullAddress?.longitude) {
        setLocationBias({
          latitude: fullAddress.latitude,
          longitude: fullAddress.longitude,
        });
      }
    },
    []
  );

  const handleNotesChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFormState((prev) => ({ ...prev, notes: event.target.value }));
    },
    []
  );

  // ============================================
  // Photo Handlers
  // ============================================

  const handleOpenCapture = useCallback(() => setIsCaptureOpen(true), []);
  const handleCloseCapture = useCallback(() => setIsCaptureOpen(false), []);

  const handlePhotoUploaded = useCallback((file: UploadedFile) => {
    setIsCaptureOpen(false);
    setFormState((prev) => ({ ...prev, uploadedFileId: file.id || null }));
    setPhotoDisplay({
      fileUrl: file.fileUrl || null,
      thumbnailUrl: file.thumbnailUrl || null,
      fileName: file.name || file.originalFilename || null,
    });
  }, []);

  const handleRemovePhoto = useCallback(() => {
    setFormState((prev) => ({ ...prev, uploadedFileId: null }));
    setPhotoDisplay(EMPTY_PHOTO_DISPLAY);
  }, []);

  const handlePreviewPhoto = useCallback(() => {
    if (photoDisplay.fileUrl) setIsPreviewOpen(true);
  }, [photoDisplay.fileUrl]);

  const handleClosePreview = useCallback(() => setIsPreviewOpen(false), []);

  // ============================================
  // Submit
  // ============================================

  const handleStart = useCallback(async () => {
    if (!formState.carId) return;

    const params: ParkingSessionInput = {
      carId: formState.carId,
      travelId: formState.travelId || undefined,
      startTime: localStringToUtc(formState.startTime),
      durationMinutes: formState.durationMinutes || undefined,
      initialPrice:
        formState.initialPrice != null && formState.initialPrice > 0
          ? formState.initialPrice
          : undefined,
      currency: formState.currency || undefined,
      formattedAddress: formState.formattedAddress || undefined,
      latitude: formState.latitude ?? undefined,
      longitude: formState.longitude ?? undefined,
      notes: formState.notes || undefined,
      uploadedFileId: formState.uploadedFileId ?? undefined,
      status: PARKING_SESSION_STATUS.ACTIVE,
    };

    setIsDirty(false);

    if (onStartProp) {
      await onStartProp(params);
    } else {
      const result = await startParking(params);
      if (result) {
        setParkingSession(null);
      }
    }
  }, [formState, onStartProp, startParking, setParkingSession]);

  // ============================================
  // Validation
  // ============================================

  const canStart = useMemo(() => {
    return !!formState.carId && !isLoading;
  }, [formState.carId, isLoading]);

  // ============================================
  // Duration preset data for SegmentedControl
  // ============================================

  const durationSegmentData = useMemo(
    () =>
      DURATION_PRESETS.map((preset) => ({
        value: preset.value,
        label: t(preset.labelKey, { defaultValue: preset.value }),
      })),
    [t]
  );

  // ============================================
  // Render
  // ============================================

  return (
    <>
      <AppDrawer
        opened={opened}
        onClose={onClose}
        title={t("parking:drawers.start.title", { defaultValue: "Start Parking" })}
        position="right"
        size="lg"
        responsive
        mobileHeightPercent={85}
        closable={!isLoading}
        loading={isLoading}
        actionType="save-cancel"
        onConfirm={handleStart}
        confirmLabel={t("parking:drawers.start.actions.start", {
          defaultValue: "Start Parking",
        })}
        confirmIcon="Parking"
        confirmDisabled={!canStart}
        isDirty={isDirty}
      >
        <Stack gap="md">
          {/* Start Time */}
          <DateTimePicker
            label={t("parking:drawers.start.fields.startTime.label", {
              defaultValue: "Start time",
            })}
            value={formState.startTime}
            onChange={handleStartTimeChange}
            maxDate={new Date()}
            size="md"
            clearable={false}
            valueFormat="MMM DD, YYYY HH:mm"
            dropdownType={isMobile ? "modal" : "popover"}
            timePickerProps={{
              withDropdown: true,
              popoverProps: { withinPortal: true },
              format: "12h",
            }}
          />

          {/* Vehicle Selector */}
          <CarSelector
            name="carId"
            value={formState.carId}
            onChange={handleCarChange}
            required
            placeholder={t("parking:drawers.start.fields.car.placeholder", {
              defaultValue: "Select vehicle",
            })}
            disabled={totalCarsCount === 1}
            withinPortal
          />

          {/* Travel Selector */}
          <Stack gap="sm">
            <TravelSelector
              name="travelId"
              value={formState.travelId}
              onChange={handleTravelChange}
              carId={formState.carId}
              preselectActiveTravel
              label={t("common:selectors.travel.label")}
              placeholder={t("common:selectors.travel.placeholder")}
              size="md"
            />
            <Text size="xs" c="dimmed">
              {t("common:selectors.travel.hint")}
            </Text>
          </Stack>

          {/* ============================================ */}
          {/* Location Section                             */}
          {/* ============================================ */}
          <Divider
            label={t("parking:drawers.start.sections.location", {
              defaultValue: "Location",
            })}
            labelPosition="center"
          />

          {/* Address Autocomplete */}
          <AddressAutocompleteInput
            value={formState.formattedAddress}
            onChange={handleLocationChange}
            locationBias={locationBias}
            label={t("parking:drawers.start.fields.address.label", {
              defaultValue: "Address",
            })}
            placeholder={t("parking:drawers.start.fields.address.placeholder", {
              defaultValue: "Where are you parked?",
            })}
            size="md"
            showLocateButton={true}
            locateButtonTooltip={t("common:buttons.useMyLocation", {
              defaultValue: "Use my location",
            })}
            withinPortal
          />

          {/* Location loading indicator */}
          {isLocationLoading && (
            <Group gap="xs" ml="xs">
              <AppIcon
                iconName="MapPin"
                size={14}
                color="var(--mantine-color-blue-6)"
              />
              <Text size="xs" c="dimmed">
                {t("common:location.detecting", {
                  defaultValue: "Detecting location...",
                })}
              </Text>
            </Group>
          )}

          {/* Map preview + coordinates */}
          {!isLocationLoading && hasCoordinates && (
            <LocationPreview
              latitude={formState.latitude!}
              longitude={formState.longitude!}
              showMap={true}
              mapHeight={140}
              zoom={16}
              showCoordinates={true}
            />
          )}

          {/* ============================================ */}
          {/* Photo Section                                */}
          {/* ============================================ */}
          <Divider
            label={t("parkingSessions:form.sections.photo", {
              defaultValue: "Photo",
            })}
            labelPosition="center"
          />

          <ParkingPhotoSection
            hasPhoto={hasPhoto}
            photoDisplay={photoDisplay}
            onOpenCapture={handleOpenCapture}
            onRemovePhoto={handleRemovePhoto}
            onPreviewPhoto={handlePreviewPhoto}
            disabled={isLoading}
          />

          {/* ============================================ */}
          {/* Duration Section                             */}
          {/* ============================================ */}
          <Divider
            label={t("parking:drawers.start.sections.duration", {
              defaultValue: "Parking Duration",
            })}
            labelPosition="center"
            size={"md"}
          />

          {/* Duration Presets */}
          <Box>
            <Text size="md" fw={500} mb={6}>
              {t("parking:drawers.start.fields.duration.label", {
                defaultValue: "How long are you parking?",
              })}
            </Text>
            <SegmentedControl
              value={formState.durationPreset}
              onChange={handleDurationPresetChange}
              data={durationSegmentData}
              fullWidth
              size="md"
            />
          </Box>

          {/* Custom duration input */}
          {showDurationInput && (
            <NumberInput
              label={t("parking:drawers.start.fields.durationMinutes.label", {
                defaultValue: "Duration (minutes)",
              })}
              placeholder={t(
                "parking:drawers.start.fields.durationMinutes.placeholder",
                { defaultValue: "Enter minutes" }
              )}
              value={formState.durationMinutes ?? ""}
              onChange={handleDurationMinutesChange}
              min={1}
              max={1440}
              size="md"
              hideControls
              rightSection={
                <Text size="sm" c="dimmed" pr="sm">
                  {t("common:units.min", { defaultValue: "min" })}
                </Text>
              }
              rightSectionWidth={50}
            />
          )}

          {/* ============================================ */}
          {/* Price Section (hidden for free/no-limit)     */}
          {/* ============================================ */}
          {showPriceSection && (
            <>
              <Divider
                label={t("parking:drawers.start.sections.price", {
                  defaultValue: "Price",
                })}
                labelPosition="center"
              />

              <Group gap="sm" align="flex-end" wrap="nowrap">
                <NumberInput
                  label={t("parking:drawers.start.fields.price.label", {
                    defaultValue: "Amount paid",
                  })}
                  placeholder={t(
                    "parking:drawers.start.fields.price.placeholder",
                    { defaultValue: "0.00" }
                  )}
                  value={formState.initialPrice ?? ""}
                  onChange={handleInitialPriceChange}
                  min={0}
                  max={99999}
                  decimalScale={2}
                  fixedDecimalScale
                  size="md"
                  hideControls
                  style={{ flex: 1 }}
                />
                <CurrencySelector
                  value={formState.currency}
                  onChange={handleCurrencyChange}
                  compact
                  compactWidth={120}
                  required
                  size="md"
                  priorityOnly
                  withinPortal
                />
              </Group>

              {/* Hint: price can be adjusted when ending */}
              <Group gap="xs" ml="xs" wrap="nowrap" style={{ alignItems: 'flex-start' }}>
                <AppIcon
                  iconName="InfoCircle"
                  size={18}
                  color="var(--mantine-color-gray-5)"
                />
                <Text size="sm" c="dimmed">
                  {t("parking:drawers.start.hints.priceAdjustable", {
                    defaultValue:
                      "You can adjust the final price when ending the session.",
                  })}
                </Text>
              </Group>
            </>
          )}

          {/* ============================================ */}
          {/* Notes                                        */}
          {/* ============================================ */}
          <Textarea
            label={t("parking:drawers.start.fields.notes.label", {
              defaultValue: "Notes",
            })}
            placeholder={t("parking:drawers.start.fields.notes.placeholder", {
              defaultValue: "Level 3, spot B12, next to the blue pillar...",
            })}
            value={formState.notes}
            onChange={handleNotesChange}
            size="md"
            maxLength={500}
            autosize
            minRows={2}
            maxRows={4}
          />
        </Stack>
      </AppDrawer>

      {/* ═══ Image Capture Flow (outside drawer portal) ═══ */}
      <ImageCaptureUpload
        opened={isCaptureOpen}
        onClose={handleCloseCapture}
        onComplete={handlePhotoUploaded}
        filePath="parking-sessions"
        cropAspectRatio="free"
        cameraTitle={t("parkingSessions:form.photo.cameraTitle", {
          defaultValue: "Parking Photo",
        })}
        cameraHint={t("parkingSessions:form.photo.cameraHint", {
          defaultValue: "Take a photo of your parking spot or ticket",
        })}
      />

      {/* ═══ Full-screen Image Preview ═══ */}
      {photoDisplay.fileUrl && (
        <ImageViewer
          opened={isPreviewOpen}
          onClose={handleClosePreview}
          src={photoDisplay.fileUrl}
          alt={
            photoDisplay.fileName ||
            t("parkingSessions:form.photo.alt", {
              defaultValue: "Parking photo",
            })
          }
          fileName={photoDisplay.fileName || undefined}
        />
      )}
    </>
  );
};

export const StartParkingDrawer = React.memo(StartParkingDrawerComponent);